/**
 * Client-safe helpers for rendering a stored song.
 *
 * Every URL here was written by the server from the catalog, but they are
 * checked again before they reach an `src`/`href`: a profile row is the kind of
 * data that outlives the code that validated it.
 */

const ARTWORK_HOST = /(^|\.)mzstatic\.com$/;
const PREVIEW_HOST = /(^|\.)(apple\.com|mzstatic\.com)$/;
const PROVIDER_HOST = /^(music|itunes|geo\.music)\.apple\.com$/;

function httpsUrl(raw: string | null | undefined, host: RegExp): URL | null {
  if (!raw) return null;
  try {
    const url = new URL(raw.replace("{w}", "1").replace("{h}", "1"));
    if (url.protocol !== "https:" || !host.test(url.hostname)) return null;
    return url;
  } catch {
    return null;
  }
}

export function isAllowedArtworkTemplate(raw: string | null | undefined) {
  return httpsUrl(raw, ARTWORK_HOST) !== null;
}

export function isAllowedPreviewUrl(raw: string | null | undefined) {
  return httpsUrl(raw, PREVIEW_HOST) !== null;
}

export function isAllowedProviderUrl(raw: string | null | undefined) {
  return httpsUrl(raw, PROVIDER_HOST) !== null;
}

/**
 * Resolve Apple's `{w}x{h}` artwork template at a pixel size. Square art only;
 * pass the RENDERED size × device pixel ratio, rounded up to a CDN-friendly
 * step so neighbouring cards share a cache entry.
 */
export function musicArtworkSrc(
  template: string | null | undefined,
  px: number,
): string | null {
  if (!template || !isAllowedArtworkTemplate(template)) return null;
  const size = Math.min(1200, Math.max(60, Math.ceil(px / 60) * 60));
  return template.replace("{w}", String(size)).replace("{h}", String(size));
}

export function safePreviewUrl(raw: string | null | undefined) {
  return isAllowedPreviewUrl(raw) ? (raw as string) : null;
}

export interface MusicServiceLink {
  service: "apple_music" | "spotify" | "youtube_music";
  label: string;
  href: string;
}

/**
 * "Open in …" for every service, without asking the visitor to connect
 * anything. Apple Music gets the exact track; Spotify and YouTube Music get a
 * search for title + artist, which lands on the track in practice and never on
 * a wrong one silently.
 */
export function musicServiceLinks(song: {
  title: string;
  artistName: string;
  providerUrl: string | null;
}): MusicServiceLink[] {
  const query = `${song.title} ${song.artistName}`.trim();
  const links: MusicServiceLink[] = [];
  if (isAllowedProviderUrl(song.providerUrl)) {
    links.push({
      service: "apple_music",
      label: "Apple Music",
      href: song.providerUrl as string,
    });
  } else {
    links.push({
      service: "apple_music",
      label: "Apple Music",
      href: `https://music.apple.com/search?term=${encodeURIComponent(query)}`,
    });
  }
  links.push(
    {
      service: "spotify",
      label: "Spotify",
      href: `https://open.spotify.com/search/${encodeURIComponent(query)}`,
    },
    {
      service: "youtube_music",
      label: "YouTube Music",
      href: `https://music.youtube.com/search?q=${encodeURIComponent(query)}`,
    },
  );
  return links;
}
